import type { DailyBriefModel } from '../daily-brief/types';
import type { ExecutiveKpiStripModel } from '../executive-kpis/types';
import type { IndiaMapModel } from '../india-map/types';
import type { MasterOverlayModel } from '../master-overlay/types';
import type { OpsIntelligenceModel } from '../ops-intelligence/types';
import type { SalesPulseModel } from '../sales-pulse-24h/types';
import { CALC_HEADERS, toCsv } from './csv';

const KPI_FORMULA: Record<string, { formula: string; source: string }> = {
  gmv: {
    formula: 'GET /orders/metrics sales (All Orders SUM(item_price) when lines exist)',
    source: 'GET /connections/{id}/orders/metrics (same as Yesterday)',
  },
  orders: {
    formula: 'GET /orders/metrics order_items (line count = Seller Central Order items)',
    source: 'GET /connections/{id}/orders/metrics',
  },
  units: {
    formula: 'GET /orders/metrics units (SUM quantity, cancelled excluded)',
    source: 'GET /connections/{id}/orders/metrics',
  },
  aov: {
    formula: 'sales ÷ order_items (same as Yesterday)',
    source: 'derived from Gate A',
  },
  asp: {
    formula: 'GMV ÷ units when both present',
    source: 'derived from scan',
  },
  inventory_available: {
    formula: 'SUM(available) on first inventory page (page_size=100)',
    source: 'GET /connections/{id}/inventory',
  },
  buy_box: {
    formula: 'winner rows / scored pricing sample, else avg buy_box_pct',
    source: 'GET /connections/{id}/pricing page 1',
  },
  business_health: {
    formula: 'composite of ready components (demand/fulfillment/inventory/platform)',
    source: 'derived',
  },
  data_confidence: {
    formula: 'coverage + readiness; −15 if scan truncated',
    source: 'derived',
  },
  return_ratio: {
    formula: 'N/A — returns domain not on this strip',
    source: 'none',
  },
};

const BRIEF_FORMULA: Record<string, { formula: string; source: string }> = {
  sales: {
    formula: 'GET /orders/metrics sales (All Orders SUM(item_price) when lines exist, else OrderTotal)',
    source: 'GET /connections/{id}/orders/metrics?from=yesterday&to=yesterday',
  },
  orders: {
    formula: 'metrics.order_items assigned to orderCount — this is LINE count, not distinct amazon-order-id',
    source: 'GET /connections/{id}/orders/metrics order_items',
  },
  units: {
    formula: 'GET /orders/metrics units (SUM quantity, cancelled excluded when All Orders)',
    source: 'GET /connections/{id}/orders/metrics units',
  },
  aov: {
    formula: 'sales ÷ order_items (uses lines as denominator, not distinct orders)',
    source: 'derived in loadDailyBrief.ts',
  },
};

export function dailyBriefCalcCsv(model: DailyBriefModel): string {
  const rows = model.metrics.map((m) => {
    const f = BRIEF_FORMULA[m.id] ?? { formula: m.reason ?? '', source: model.sourceNote };
    return [
      'daily-brief',
      m.id,
      m.value,
      m.prior,
      f.formula,
      f.source,
      '',
      '',
      model.connectionId,
      [model.sourceNote, m.reason, model.coverage.ordersTruncated ? 'scan_truncated' : '']
        .filter(Boolean)
        .join('; '),
    ];
  });
  rows.push([
    'daily-brief',
    'health_score',
    model.health.overall,
    null,
    'composite ≥3 ready components',
    'derived healthScore.ts',
    '',
    '',
    model.connectionId,
    `band=${model.health.band}; conf=${model.health.confidence}`,
  ]);
  return toCsv([...CALC_HEADERS], rows);
}

export function executiveKpiCalcCsv(model: ExecutiveKpiStripModel): string {
  const rows = model.kpis.map((k) => {
    const f = KPI_FORMULA[k.id] ?? { formula: k.tooltip, source: 'unknown' };
    return [
      'executive-kpis',
      k.id,
      k.value,
      k.prior,
      f.formula,
      f.source,
      model.from,
      model.to,
      model.connectionId,
      [k.reason, model.truncated ? 'scan_truncated=true' : ''].filter(Boolean).join('; '),
    ];
  });
  return toCsv([...CALC_HEADERS], rows);
}

export function overlayCalcCsv(model: MasterOverlayModel): string {
  const rows: Array<Array<string | number | boolean | null | undefined>> = model.metrics.map((m) => [
    'master-overlay',
    m.id,
    null,
    null,
    'GET /orders/metrics/series grain=day (All Orders SUM(item_price) / order items / units)',
    'GET /connections/{id}/orders/metrics/series',
    model.from,
    model.to,
    model.connectionId,
    m.available ? '' : (m.disabledReason ?? 'unavailable'),
  ]);
  for (const p of model.points) {
    for (const m of model.metrics) {
      rows.push([
        'master-overlay-day',
        `${p.iso}:${m.id}`,
        p.values[m.id] ?? null,
        p.priorValues[m.id] ?? null,
        'Gate A series bucket for that IST calendar day',
        model.sourceNote ?? 'GET /orders/metrics/series',
        p.iso,
        p.iso,
        model.connectionId,
        model.truncated ? 'scan_truncated' : '',
      ]);
    }
  }
  return toCsv([...CALC_HEADERS], rows);
}

export function pulseCalcCsv(model: SalesPulseModel): string {
  const rows: Array<Array<string | number | boolean | null | undefined>> = [
    [
      'sales-pulse-24h',
      'gmv',
      null,
      null,
      'SUM(item_price) of All Orders lines grouped by IST hour (Gate A series grain=hour)',
      model.sourceNote ?? 'GET /orders/metrics/series?grain=hour',
      model.dayIso,
      model.dayIso,
      model.connectionId,
      model.truncated ? 'scan_truncated' : '',
    ],
    [
      'sales-pulse-24h',
      'orders',
      null,
      null,
      'SUM(order_items) per IST hour from Gate A series',
      model.sourceNote ?? 'GET /orders/metrics/series?grain=hour',
      model.dayIso,
      model.dayIso,
      model.connectionId,
      '',
    ],
    [
      'sales-pulse-24h',
      'units',
      null,
      null,
      'SUM(quantity) per IST hour from Gate A series',
      model.sourceNote ?? 'GET /orders/metrics/series?grain=hour',
      model.dayIso,
      model.dayIso,
      model.connectionId,
      '',
    ],
  ];
  for (const p of model.points) {
    rows.push([
      'sales-pulse-hour',
      `h${String(p.hour).padStart(2, '0')}_today_gmv`,
      p.today.gmv,
      p.yesterday.gmv,
      'SUM(OrderTotal) headers in that local hour',
      model.sourceNote ?? 'GET /orders scan',
      model.dayIso,
      model.dayIso,
      model.connectionId,
      `orders=${p.today.orders}; units=${p.today.units ?? ''}`,
    ]);
  }
  return toCsv([...CALC_HEADERS], rows);
}

export function opsCalcCsv(model: OpsIntelligenceModel): string {
  const rows: Array<Array<string | number | boolean | null | undefined>> = (model.items ?? []).map(
    (a) => [
      'ops-intelligence',
      a.id,
      null,
      null,
      'ruleEngine on yesterday scan aggregates + inventory sample',
      'GET /orders scan + GET /inventory page 1',
      '',
      '',
      model.connectionId,
      `${a.priority}: ${a.title}; ${a.evidence}`,
    ],
  );
  if (rows.length === 0) {
    rows.push([
      'ops-intelligence',
      'empty',
      null,
      null,
      'no actions',
      'GET /orders scan',
      '',
      '',
      model.connectionId,
      model.error ?? model.sourceNote ?? '',
    ]);
  }
  return toCsv([...CALC_HEADERS], rows);
}

export function indiaMapCalcCsv(model: IndiaMapModel): string {
  const rows: Array<Array<string | number | boolean | null | undefined>> = [
    [
      'india-map',
      'total_gmv_geo',
      model.totalGmvKnownGeo,
      null,
      'SUM(item_price) by ship-state when All Orders geo API has lines; else OrderTotal from scan',
      model.source ?? 'GET /spapi-v2/all-orders/geo or orders scan fallback',
      model.periodFrom,
      model.periodTo,
      model.connectionId,
      `coverage=${model.addressCoveragePct}; heatGated=${model.heatGated}`,
    ],
  ];
  for (const c of model.topCities) {
    rows.push([
      'india-map-city',
      c.city,
      c.gmv,
      c.orders,
      'SUM(item_price), COUNT DISTINCT order id, grouped by ship-city',
      model.source ?? 'all_orders_flat_file',
      model.periodFrom,
      model.periodTo,
      model.connectionId,
      c.state ?? '',
    ]);
  }
  for (const s of model.states.filter((x) => x.hasData)) {
    rows.push([
      'india-map-state',
      s.code,
      s.gmv,
      s.orderCount,
      'rollup of ship-state',
      model.source ?? 'all_orders_flat_file',
      model.periodFrom,
      model.periodTo,
      model.connectionId,
      s.name,
    ]);
  }
  return toCsv([...CALC_HEADERS], rows);
}
